import { useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { portalApi } from '../api';
import type { ContentHomeOut, PortalTab, PublicContentListItem } from '../types';
import { ContentCard } from './ContentCard';
import { ContentDetailOverlay } from './ContentDetailOverlay';

interface PortalHomeProps {
  onTabChange: (tab: PortalTab) => void;
}

export const PortalHome = ({ onTabChange }: PortalHomeProps) => {
  const { t } = useI18n();
  const [data, setData] = useState<ContentHomeOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    portalApi.home()
      .then(setData)
      .catch(() => setData({ latest_feed: [], new_opportunities: [], new_materials: [], important: [], trending: [], popular_hashtags: [], subscribed_hashtags: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-dim)' }}>Загрузка...</div>;
  }

  const Row = ({ title, items, tab }: { title: string; items: PublicContentListItem[]; tab: PortalTab }) => (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{title}</h3>
        <button
          onClick={() => onTabChange(tab)}
          style={{ fontSize: 12, fontWeight: 700, color: '#41d2ff', background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          {t('portal.home.viewAll' as any)}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="public-empty">{t('portal.filter.noResults' as any)}</div>
      ) : (
        <div className="public-results">
          {items.slice(0, 3).map(item => (
            <ContentCard key={item.id} item={item} onClick={i => setOpenId(i.id)} compact />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="public-hero" style={{ marginBottom: 28 }}>
        <div>
          <span className="public-eyebrow">Портал ZERTTEU ACADEMY</span>
          <h2>{t('portal.home.hero.title' as any)}</h2>
          <p>{t('portal.home.hero.subtitle' as any)}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="public-badge">🎓 ACADEMIA</span>
          <span className="public-badge">🔬 SCIENCE</span>
          <span className="public-badge">🌐 OPEN</span>
        </div>
      </div>

      {data?.important && data.important.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#fbbf24' }}>
            ⚡ {t('portal.home.important' as any)}
          </h3>
          <div className="public-results">
            {data.important.map(item => (
              <ContentCard key={item.id} item={item} onClick={i => setOpenId(i.id)} compact />
            ))}
          </div>
        </div>
      )}

      {data?.trending && data.trending.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>🔥 Тренды</h3>
          </div>
          <div className="public-results">
            {data.trending.slice(0, 4).map(item => (
              <ContentCard key={item.id} item={item} onClick={i => setOpenId(i.id)} compact />
            ))}
          </div>
        </div>
      )}

      {data?.subscribed_hashtags && data.subscribed_hashtags.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 800, color: 'var(--text-soft)' }}>
            Мои подписки
          </h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.subscribed_hashtags.map(tag => (
              <span key={tag} style={{ fontSize: 12, color: '#49ddb7', background: '#49ddb715', padding: '5px 12px', borderRadius: 20 }}>
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {data && <Row title={t('portal.home.latestFeed' as any)} items={data.latest_feed} tab="news" />}
      {data && <Row title={t('portal.home.opportunities' as any)} items={data.new_opportunities} tab="competitions" />}
      {data && <Row title={t('portal.home.materials' as any)} items={data.new_materials} tab="materials" />}

      {data?.popular_hashtags && data.popular_hashtags.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 800, color: 'var(--text-soft)' }}>
            # {t('portal.home.popularTags' as any)}
          </h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.popular_hashtags.map(tag => (
              <span key={tag} style={{ fontSize: 12, color: '#41d2ff', background: '#41d2ff15', padding: '5px 12px', borderRadius: 20, cursor: 'pointer' }}>
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {openId && <ContentDetailOverlay contentId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};
